import {
  CategoryType,
  FeatureType,
  NestedSearch,
  NearbyType,
  NestedAgency,
  StatusType,
} from '@habity/types';
import {
  getCategoryText,
  getLivingSpace,
  getPlotSize,
  getRooms,
} from '../addresses';
import { formatCurrency } from '@habity/utils';
import { FEATURES, NEARBY } from '@habity/constants';

export function getSearchDescription(
  search: Partial<NestedSearch>,
  agency: NestedAgency | null,
  includeLocation = false,
  maxLength?: number
) {
  const {
    city,
    zips,
    streets,
    municipality,
    rooms_min,
    rooms_max,
    plot_size_min,
    plot_size_max,
    living_space_min,
    living_space_max,
    build_year_min,
    build_year_max,
    price_min,
    price_max,
    categories,
    is_rental_unit,
    statuses,
    features,
    nearby,
    places,
    label,
  } = search || {};
  const t: string[] = [];

  // Location
  if (includeLocation) {
    if (municipality) t.push(municipality);
    if (city) t.push(city);
    if (zips && zips.length > 0) t.push(zips.join(', '));
    if (streets && streets.length > 0) t.push(streets.join(', '));
  }

  // Categories
  if (categories && categories.length > 0)
    t.push(categories.map((c: CategoryType) => getCategoryText(c)).join(', '));

  // Statuses
  const { statuses: appStatuses } = agency || {};
  if (statuses && statuses.length > 0)
    t.push(
      statuses
        .map((s: StatusType) => appStatuses?.find((as) => as.name === s)?.title)
        .join(', ')
    );

  // Features
  if (features && features.length > 0)
    t.push(
      features
        .map((f: FeatureType) => {
          const title = FEATURES.find((b) => b.key === f)?.title;
          return title || f;
        })
        .join(', ')
    );

  // Nearby
  if (nearby && nearby.length > 0)
    t.push(
      nearby
        .map((f: NearbyType) => {
          const title = NEARBY.find((b) => b.key === f)?.title;
          return title || f;
        })
        .join(', ')
    );

  // Price
  if (price_min && price_max) {
    t.push(
      formatCurrency(price_min).concat('-').concat(formatCurrency(price_max))
    );
  } else if (price_min) {
    t.push(' > '.concat(formatCurrency(price_min)));
  } else if (price_max) {
    t.push(' < '.concat(formatCurrency(price_max)));
  }
  // Rooms
  if (rooms_min && rooms_max) {
    t.push(rooms_min.toString().concat('-').concat(getRooms(rooms_max)));
  } else if (rooms_min) {
    t.push(` > ${getRooms(rooms_min)}`);
  } else if (rooms_max) {
    t.push(` < ${getRooms(rooms_max)}`);
  }
  // Living space
  if (living_space_min && living_space_max) {
    t.push(
      living_space_min
        .toString()
        .concat('-')
        .concat(getLivingSpace(living_space_max))
    );
  } else if (living_space_min) {
    t.push(' > '.concat(getLivingSpace(living_space_min)));
  } else if (living_space_max) {
    t.push(' < '.concat(getLivingSpace(living_space_max)));
  }
  // Plot size
  if (plot_size_min && plot_size_max) {
    t.push(
      plot_size_min.toString().concat('-').concat(getPlotSize(plot_size_max))
    );
  } else if (plot_size_min) {
    t.push(' > '.concat(getPlotSize(plot_size_min)));
  } else if (plot_size_max) {
    t.push(' < '.concat(getPlotSize(plot_size_max)));
  }
  // Plot size
  if (build_year_min && build_year_max) {
    t.push(
      'byggår '
        .concat(build_year_min.toString())
        .concat('-')
        .concat(build_year_max.toString())
    );
  } else if (build_year_min) {
    t.push('byggår > '.concat(build_year_min.toString()));
  } else if (build_year_max) {
    t.push('byggår < '.concat(build_year_max.toString()));
  }

  if (is_rental_unit) t.push('Hyresrätt');

  if (places && t.length === 0) {
    if (label) {
      t.push(label);
    } else {
      t.push(places.join(', '));
    }
  }

  let title = t.join(', ');

  if (maxLength) {
    title =
      title.length > maxLength
        ? title.slice(0, maxLength).concat('...')
        : title;
  }

  return title;
};

